import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import { Link, useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import {
  AppTable,
  AppVideoPlayer,
  ColumnHeader,
  DateColumn,
  TextColumn,
} from "../../components";
import {
  LiveLectureAccessModeEnum,
  LiveLectureBookingStatusEnum,
  LiveLectureStatusEnum,
  MediaTypeEnum,
} from "../../enums";
import { LiveLectureBookingAdminDto } from "../../interfaces";
import { MediaDto, MediaStatusEnum } from "../../interfaces/media";
import {
  useGetLiveLectureBookingsQuery,
  useGetLiveLectureQuery,
  useLazyGetBookingMeetingLinkQuery,
  useLazyGetLiveLectureSessionActivityQuery,
  useLazyGetSessionMeetingLinkQuery,
} from "../../store/api/liveLectureApi";
import {
  canJoinLiveLecture,
  canManageLiveLecture,
  formatLiveLectureDate,
  formatLiveLectureTimeSlot,
  canEditLiveLecture,
  isPastLiveLecture,
  LiveLectureManagerProfile,
} from "./liveLectureCalendarUtils";
import { openLiveCall } from "./openLiveCall";
import { LiveLectureStatusDot } from "./LiveLectureStatusDot";
import { LiveLectureInvitationsPanel } from "./LiveLectureInvitationsPanel";
import imagePlaceholder from "../../assets/images/placeholder/image-placeholder.png";

type TProps = {
  liveLectureId?: string | null;
  show: boolean;
  onCloseClick: () => void;
  profile?: LiveLectureManagerProfile;
  onEditClick?: () => void;
};

const joinableStatuses = [
  LiveLectureStatusEnum.PUBLISHED,
  LiveLectureStatusEnum.FULL,
  LiveLectureStatusEnum.IN_PROGRESS,
];

export const LiveLectureDetailModal: React.FC<TProps> = ({
  liveLectureId,
  show,
  onCloseClick,
  profile,
  onEditClick,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const lang = i18n.language === "ar" ? "ar" : "en";
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string>();
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const { data: lecture, isLoading: isLoadingLecture } = useGetLiveLectureQuery(
    liveLectureId as string,
    { skip: !show || !liveLectureId },
  );

  const isSubjectIncluded =
    lecture?.accessMode === LiveLectureAccessModeEnum.SUBJECT_INCLUDED;
  const isPrivate = lecture?.accessMode === LiveLectureAccessModeEnum.PRIVATE;

  const {
    data: bookingsData,
    isLoading: isLoadingBookings,
    isFetching: isFetchingBookings,
    refetch: refetchBookings,
  } = useGetLiveLectureBookingsQuery(liveLectureId as string, {
    skip:
      !show ||
      !liveLectureId ||
      isLoadingLecture ||
      isSubjectIncluded ||
      isPrivate,
  });

  const [
    loadSessionActivity,
    {
      data: sessionActivity,
      isFetching: isLoadingActivity,
      isError: isActivityError,
    },
  ] = useLazyGetLiveLectureSessionActivityQuery();
  const [getSessionMeetingLink, { isFetching: isJoiningSession }] =
    useLazyGetSessionMeetingLinkQuery();
  const [getBookingMeetingLink, { isFetching: isJoiningBooking }] =
    useLazyGetBookingMeetingLinkQuery();

  const canManage = canManageLiveLecture(lecture, profile);
  const isPreviousLecture = lecture
    ? isPastLiveLecture(lecture.endAt)
    : false;
  const showSessionActivity =
    Boolean(lecture) &&
    (isPreviousLecture ||
      lecture?.status === LiveLectureStatusEnum.IN_PROGRESS ||
      new Date(lecture?.startAt ?? 0).getTime() <= Date.now());

  useEffect(() => {
    setActivityOpen(false);
  }, [liveLectureId, show]);

  const openSessionActivity = () => {
    if (!liveLectureId) return;
    setActivityOpen(true);
    loadSessionActivity(liveLectureId);
  };

  const visibleActivity =
    sessionActivity?.liveLectureId === liveLectureId
      ? sessionActivity
      : undefined;

  const onRecordingAction = () => {
    toast.info(
      t("liveLectureRecordingNotAvailable", {
        defaultValue: "Recording is not available yet.",
      }),
    );
  };

  const showJoinButton =
    canJoinLiveLecture(profile) &&
    lecture &&
    joinableStatuses.includes(lecture.status);

  const galleryItems = useMemo(
    () =>
      (lecture?.media || []).filter(
        (item) => item.type !== MediaTypeEnum.DOCUMENT,
      ),
    [lecture?.media],
  );

  const noteItems = useMemo(
    () =>
      (lecture?.media || []).filter(
        (item) => item.type === MediaTypeEnum.DOCUMENT,
      ),
    [lecture?.media],
  );

  const getUserName = (name?: { en: string; ar: string }) =>
    name ? name[lang] : "-";

  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case "CONFIRMED":
      case "ATTENDED":
        return "success";
      case "PENDING_PAYMENT":
        return "warning";
      case "CANCELLED":
      case "REFUNDED":
        return "danger";
      case "NO_SHOW":
        return "secondary";
      default:
        return "primary";
    }
  };

  const bookingColumns: TableColumn<LiveLectureBookingAdminDto>[] = [
    {
      name: <ColumnHeader title="student" />,
      cell: (row) => (
        <div>
          <div className="fw-medium">
            {getUserName(row.student?.name) || "-"}
          </div>
          {row.student?.phone && (
            <div className="text-muted small">{row.student.phone}</div>
          )}
        </div>
      ),
    },
    {
      name: <ColumnHeader title="bookedBy" />,
      cell: (row) => (
        <div>
          <div>{getUserName(row.bookedBy?.name) || "-"}</div>
          {row.bookedBy?.phone && (
            <div className="text-muted small">{row.bookedBy.phone}</div>
          )}
        </div>
      ),
    },
    {
      name: <ColumnHeader title="status" />,
      cell: (row) => (
        <Badge color={getBookingStatusColor(row.status)}>
          {t(`liveLectureBookingStatus_${row.status}`)}
        </Badge>
      ),
    },
    {
      name: <ColumnHeader title="bookedAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
    },
    {
      name: <ColumnHeader title="payment" />,
      cell: (row) => (
        <TextColumn
          text={
            row.bookingPaymentMode
              ? t(`liveLectureBookingPaymentMode_${row.bookingPaymentMode}`)
              : "-"
          }
        />
      ),
    },
    {
      name: <ColumnHeader title="total" />,
      cell: (row) => (
        <TextColumn text={row.total > 0 ? row.total.toFixed(2) : "-"} />
      ),
    },
    {
      name: <ColumnHeader title="studyNotes" />,
      cell: (row) => (
        <TextColumn text={row.studyNotes || "-"} isNeedToTranslate={false} />
      ),
    },
    {
      name: <ColumnHeader title="participants" />,
      cell: (row) => (
        <TextColumn
          text={
            row.participants?.length
              ? row.participants
                  .map(
                    (participant) =>
                      participant.participantName ||
                      participant.participantEmail ||
                      "-",
                  )
                  .join(", ")
              : "-"
          }
          isNeedToTranslate={false}
        />
      ),
    },
  ];

  const getPreviewSrc = (item: MediaDto) => {
    if (item.type === MediaTypeEnum.VIDEO || item.videoUrl || item.thumbnail) {
      return item.thumbnail || imagePlaceholder;
    }
    return item.path || imagePlaceholder;
  };

  const getPlayableUrl = (item: MediaDto) => {
    if (item.type === MediaTypeEnum.VIDEO || item.videoUrl) {
      if (item.status === MediaStatusEnum.COMPLETED && item.videoUrl) {
        return item.videoUrl;
      }
      return item.path;
    }
    return item.path;
  };

  const handleJoin = async () => {
    if (!liveLectureId) return;

    try {
      const sessionLink = await getSessionMeetingLink(liveLectureId).unwrap();
      if (sessionLink.canJoin && sessionLink.token) {
        const joined = await openLiveCall(sessionLink, history, {
          title: lecture?.title?.[lang],
          returnPath: history.location.pathname,
        });
        if (!joined) {
          toast.error(t("liveCallMicPermissionDenied"));
          return;
        }
        return;
      }
    } catch {
      if (isSubjectIncluded) {
        toast.error(t("joinLiveLectureError"));
        return;
      }
      // Fall back to booking-based join for student-booked standalone sessions.
    }

    const confirmedBooking = bookingsData?.bookings.find(
      (booking) => booking.status === LiveLectureBookingStatusEnum.CONFIRMED,
    );

    if (!confirmedBooking) {
      toast.error(t("joinLiveLectureNotAvailable"));
      return;
    }

    try {
      const bookingLink = await getBookingMeetingLink(
        confirmedBooking.id,
      ).unwrap();
      if (bookingLink.canJoin && bookingLink.token) {
        const joined = await openLiveCall(bookingLink, history, {
          title: lecture?.title?.[lang],
          returnPath: history.location.pathname,
        });
        if (!joined) {
          toast.error(t("liveCallMicPermissionDenied"));
        }
      } else {
        toast.error(t("joinLiveLectureNotAvailable"));
      }
    } catch {
      toast.error(t("joinLiveLectureError"));
    }
  };

  const renderInfoItem = (label: string, value: React.ReactNode) => (
    <Col sm={6} md={4} className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="fw-medium">{value}</div>
    </Col>
  );

  const descriptionHtml =
    lang === "ar" ? lecture?.description?.ar : lecture?.description?.en;

  const academicPathSegments = [
    {
      label: t("educationSystem"),
      value: getUserName(lecture?.academicPath?.educationSystem?.name),
    },
    {
      label: t("curriculumItem"),
      value: getUserName(lecture?.academicPath?.curriculum?.name),
    },
    {
      label: t("educationalStage"),
      value: getUserName(lecture?.academicPath?.stage?.name),
    },
    {
      label: t("course"),
      value: getUserName(lecture?.academicPath?.course?.name),
    },
    {
      label: t("grade"),
      value: getUserName(lecture?.academicPath?.grade?.name),
    },
    {
      label: t("subject"),
      value: lecture?.subject ? getUserName(lecture.subject.name) : "",
    },
  ].filter((segment) => segment.value && segment.value !== "-");

  const isJoining = isJoiningSession || isJoiningBooking;

  return (
    <React.Fragment>
      <Modal isOpen={show} toggle={onCloseClick} centered size="xl">
        <ModalHeader toggle={onCloseClick}>
          {t("liveLectureDetails")}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {isLoadingLecture ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : lecture ? (
            <React.Fragment>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h5 className="mb-0">{lecture.title[lang]}</h5>
                <LiveLectureStatusDot status={lecture.status} size={10} />
                <span className="small text-muted">
                  {t(`liveLectureStatus_${lecture.status}`)}
                </span>
                <Badge
                  color={
                    isPrivate ? "dark" : isSubjectIncluded ? "info" : "primary"
                  }
                >
                  {t(`liveLectureAccessMode_${lecture.accessMode}`)}
                </Badge>
              </div>

              {showSessionActivity ? (
                <div className="mb-4">
                  {!activityOpen ? (
                    <Button
                      color="soft-primary"
                      size="sm"
                      onClick={openSessionActivity}
                    >
                      <i className="ri-group-line me-1"></i>
                      {t("liveLectureSessionActivity", {
                        defaultValue: "Session details",
                      })}
                    </Button>
                  ) : (
                    <div className="border rounded p-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="mb-0">
                          {t("liveLectureSessionActivity", {
                            defaultValue: "Session details",
                          })}
                        </h6>
                        <Button
                          color="soft-success"
                          size="sm"
                          onClick={() =>
                            liveLectureId && loadSessionActivity(liveLectureId)
                          }
                          disabled={isLoadingActivity}
                        >
                          {isLoadingActivity ? (
                            <Spinner size="sm" />
                          ) : (
                            t("refresh")
                          )}
                        </Button>
                      </div>
                      {isLoadingActivity && !visibleActivity ? (
                        <div className="text-center py-3">
                          <Spinner size="sm" />
                        </div>
                      ) : isActivityError && !visibleActivity ? (
                        <div className="text-danger small">
                          {t("joinLiveLectureError")}
                        </div>
                      ) : visibleActivity ? (
                        <React.Fragment>
                          <Row>
                            {renderInfoItem(
                              t("liveLectureStartedAt", {
                                defaultValue: "Started at",
                              }),
                              visibleActivity.startedAt
                                ? moment(visibleActivity.startedAt).format(
                                    "lll",
                                  )
                                : t("liveLectureNotStarted", {
                                    defaultValue: "Not started",
                                  }),
                            )}
                            {renderInfoItem(
                              t("liveLectureEndedAt", {
                                defaultValue: "Ended at",
                              }),
                              visibleActivity.endedAt
                                ? moment(visibleActivity.endedAt).format("lll")
                                : visibleActivity.isOngoing
                                  ? t("liveLectureStillOngoing", {
                                      defaultValue: "Still ongoing",
                                    })
                                  : "-",
                            )}
                            {renderInfoItem(
                              t("participants"),
                              visibleActivity.participants.length,
                            )}
                          </Row>
                          {visibleActivity.participants.length ? (
                            <div className="d-flex flex-column gap-2">
                              {visibleActivity.participants.map(
                                (participant) => (
                                  <div
                                    key={participant.id}
                                    className="d-flex flex-wrap justify-content-between gap-2 border rounded px-3 py-2"
                                  >
                                    <div>
                                      <div className="fw-medium">
                                        {participant.name}
                                        {participant.isGuest ? (
                                          <Badge
                                            color="secondary"
                                            className="ms-2"
                                          >
                                            {t("guestName")}
                                          </Badge>
                                        ) : null}
                                      </div>
                                      <div className="text-muted small">
                                        {t("liveLectureJoinCount", {
                                          defaultValue: "Joins",
                                        })}
                                        : {participant.joinCount}
                                      </div>
                                    </div>
                                    <div className="text-muted small text-end">
                                      <div>
                                        {t("firstJoinedAt")}:{" "}
                                        {participant.firstJoinedAt
                                          ? moment(
                                              participant.firstJoinedAt,
                                            ).format("lll")
                                          : "-"}
                                      </div>
                                      <div>
                                        {t("lastJoinedAt", {
                                          defaultValue: "Last joined",
                                        })}
                                        :{" "}
                                        {participant.lastJoinedAt
                                          ? moment(
                                              participant.lastJoinedAt,
                                            ).format("lll")
                                          : "-"}
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <div className="text-muted small">
                              {t("liveLectureNoParticipants", {
                                defaultValue:
                                  "No one has joined this lecture yet.",
                              })}
                            </div>
                          )}
                        </React.Fragment>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : null}

              {isPreviousLecture ? (
                <div className="border rounded p-3 mb-4 bg-light">
                  <div className="fw-medium mb-1">
                    {t("liveLectureRecording", { defaultValue: "Recording" })}
                  </div>
                  <div className="text-muted small mb-2">
                    {t("liveLectureRecordingNotAvailable", {
                      defaultValue: "Recording is not available yet.",
                    })}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="info" size="sm" onClick={onRecordingAction}>
                      <i className="ri-eye-line me-1"></i>
                      {t("previewLiveLectureRecording", {
                        defaultValue: "Preview",
                      })}
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={onRecordingAction}
                    >
                      <i className="ri-play-circle-line me-1"></i>
                      {t("watchLiveLectureRecording", {
                        defaultValue: "Watch recording",
                      })}
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      onClick={onRecordingAction}
                    >
                      <i className="ri-download-2-line me-1"></i>
                      {t("downloadLiveLectureRecording", {
                        defaultValue: "Download recording",
                      })}
                    </Button>
                  </div>
                </div>
              ) : null}

              {academicPathSegments.length > 0 ? (
                <div className="border rounded p-3 mb-4 bg-light">
                  <div className="text-muted small mb-2">
                    {t("liveLectureAcademicPath", {
                      defaultValue: "Academic path",
                    })}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {academicPathSegments.map((segment, index) => (
                      <React.Fragment key={segment.label}>
                        {index > 0 ? (
                          <span className="text-muted">/</span>
                        ) : null}
                        <div>
                          <div className="text-muted small">{segment.label}</div>
                          <div className="fw-medium">{segment.value}</div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : null}

              <Row>
                {renderInfoItem(
                  t("teacher"),
                  getUserName(lecture.teacher?.name),
                )}
                {renderInfoItem(
                  t("liveLectureType"),
                  lecture.type ? t(`liveLectureType_${lecture.type}`) : "-",
                )}
                {renderInfoItem(
                  t("startAt"),
                  <React.Fragment>
                    <div>{formatLiveLectureDate(lecture.startAt)}</div>
                    <div className="text-muted small">
                      {formatLiveLectureTimeSlot(
                        lecture.startAt,
                        lecture.endAt,
                      )}
                    </div>
                  </React.Fragment>,
                )}
                {renderInfoItem(
                  t("duration"),
                  `${lecture.durationMinutes} ${t("minutes")}`,
                )}
                {!isSubjectIncluded &&
                  !isPrivate &&
                  renderInfoItem(t("bookedCount"), lecture.bookedCount)}
                {!isSubjectIncluded &&
                  !isPrivate &&
                  renderInfoItem(
                    t("maxParticipants"),
                    lecture.maxParticipants ?? "-",
                  )}
                {!isSubjectIncluded &&
                  !isPrivate &&
                  renderInfoItem(
                    t("price"),
                    `${lecture.price} ${lecture.currency}`,
                  )}
                {renderInfoItem(
                  t("createdAt"),
                  moment(lecture.createdAt).format("lll"),
                )}
              </Row>

              {descriptionHtml ? (
                <div className="mb-4">
                  <div className="text-muted small mb-2">
                    {t("description")}
                  </div>
                  <div
                    className="border rounded p-3 bg-light"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                </div>
              ) : null}

              {canManage && galleryItems.length > 0 ? (
                <div className="mb-4">
                  <h6 className="mb-2">
                    {t("liveLectureGalleryAndRecordings")}
                  </h6>
                  <Row className="g-3">
                    {galleryItems.map((item) => (
                      <Col sm={6} md={4} lg={3} key={item.id}>
                        <div className="border rounded p-2 h-100">
                          <img
                            src={getPreviewSrc(item)}
                            alt={item.name}
                            className="img-fluid rounded mb-2"
                            style={{ maxHeight: 140, objectFit: "cover" }}
                          />
                          <div className="small text-truncate">{item.name}</div>
                          {(item.type === MediaTypeEnum.VIDEO ||
                            item.videoUrl) && (
                            <Button
                              color="soft-primary"
                              size="sm"
                              className="mt-2"
                              onClick={() => {
                                setPlayingVideoUrl(getPlayableUrl(item));
                                setShowPlayerModal(true);
                              }}
                            >
                              <i className="ri-play-circle-line me-1"></i>
                              {t("play")}
                            </Button>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : null}

              {noteItems.length > 0 ? (
                <div className="mb-4">
                  <h6 className="mb-2">{t("liveLectureNotes")}</h6>
                  <div className="d-flex flex-column gap-2">
                    {noteItems.map((item) => (
                      <a
                        key={item.id}
                        href={item.path}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-soft-secondary btn-sm align-self-start"
                      >
                        <i className="ri-file-pdf-line me-1"></i>
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              {isPrivate && liveLectureId ? (
                <LiveLectureInvitationsPanel liveLectureId={liveLectureId} />
              ) : null}

              {!isSubjectIncluded && !isPrivate ? (
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0">{t("liveLectureBookings")}</h6>
                    <Button
                      color="soft-success"
                      size="sm"
                      onClick={() => refetchBookings()}
                      disabled={isFetchingBookings}
                    >
                      {isFetchingBookings ? (
                        <Spinner size="sm" />
                      ) : (
                        t("refresh")
                      )}
                    </Button>
                  </div>

                  {isLoadingBookings ? (
                    <div className="text-center py-4">
                      <Spinner />
                    </div>
                  ) : bookingsData?.bookings.length ? (
                    <AppTable
                      columns={bookingColumns}
                      data={bookingsData.bookings}
                      loading={isFetchingBookings}
                      pagination={false}
                    />
                  ) : (
                    <div className="text-center text-muted py-4">
                      {t("noLiveLectureBookings")}
                    </div>
                  )}
                </div>
              ) : null}
            </React.Fragment>
          ) : (
            <div className="text-center text-muted py-4">{t("notFound")}</div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div className="d-flex flex-wrap gap-2">
            {canManage && onEditClick && canEditLiveLecture(lecture) && (
              <Button
                color="success"
                onClick={onEditClick}
                className="d-flex justify-content-center"
              >
                <i className="ri-edit-box-line me-1"></i>
                {t("editItem", { item: "liveLecture" })}
              </Button>
            )}
            {showJoinButton && (
              <Button
                color="primary"
                onClick={handleJoin}
                disabled={isJoining}
                className="d-flex justify-content-center"
              >
                {isJoining ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <i className="ri-live-line me-1"></i>
                )}
                {t("joinLiveLecture")}
              </Button>
            )}
          </div>
          <Link
            to="#"
            onClick={onCloseClick}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showPlayerModal}
        toggle={() => setShowPlayerModal(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setShowPlayerModal(false)}>
          {t("play")}
        </ModalHeader>
        <ModalBody>
          {playingVideoUrl ? <AppVideoPlayer src={playingVideoUrl} /> : null}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};
